'use client';

import useSWR from 'swr';
import { apiFetch, swrFetcher } from './api';

export type CartItem = {
  id: number;
  productId: number;
  variantId: number | null;
  variantName: string | null;
  name: string;
  slug: string;
  imageAssetId: number | null;
  unitPrice: number;
  currency: string;
  quantity: number;
  lineTotal: number;
};

export type Cart = {
  id: number | null;
  items: CartItem[];
  subtotal: number;
  currency: string;
  itemCount: number;
};

const EMPTY: Cart = { id: null, items: [], subtotal: 0, currency: 'CZK', itemCount: 0 };

/** Server-side eshop cart — identified by the `eshop_cart` HttpOnly cookie. */
export function useEshopCart(enabled: boolean) {
  const { data, mutate, isLoading } = useSWR<Cart>(
    enabled ? '/api/public/eshop/cart' : null,
    swrFetcher
  );

  const addItem = async (productId: number, quantity = 1, variantId?: number | null) => {
    await apiFetch('/api/public/eshop/cart/items', {
      method: 'POST',
      body: JSON.stringify({ productId, quantity, variantId: variantId ?? null }),
    });
    mutate();
  };

  const updateItem = async (itemId: number, quantity: number) => {
    await apiFetch(`/api/public/eshop/cart/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
    mutate();
  };

  const removeItem = async (itemId: number) => {
    await apiFetch(`/api/public/eshop/cart/items/${itemId}`, { method: 'DELETE' });
    mutate();
  };

  return { cart: data ?? EMPTY, isLoading, addItem, updateItem, removeItem, mutate };
}
